import { Controller } from "@hotwired/stimulus"
import { openChrome, closeChrome, closeChromeThenNavigate } from "../helpers/overlay_chrome"

// The matching-expenses deck (budgets/_match_review_deck):
// bank_review_deck_controller's shell — the same open, entrance, native
// chrome and cache reset — with an answer per card instead of one Dismiss.
//
// Merge and Keep post their card's URL (ExpenseMatchesController) with fetch,
// the due deck's way (due_deck_controller#_post), and the card leaves the
// list once the server took it. A refusal leaves the card where it is and the
// next tap tries again. Later — the CTA, or a tap on the scrim — closes:
// locally when nothing was answered, otherwise with a dashboard re-fetch (a
// Turbo replace visit, after every answer has landed) so the strip recounts
// and the streak +1 card plays on that render. There is never a frame of
// bare dashboard: the page stays until the next one has arrived.
export default class extends Controller {
  static targets = ["stage", "mascot", "reveal", "card"]

  static EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)"
  static EASE_SPRING = "cubic-bezier(0.2, 1.55, 0.35, 1)"

  connect() {
    this._requests = []
    this._resetForCache = () => this._reset()
    document.addEventListener("turbo:before-cache", this._resetForCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._resetForCache)
    this._unlockScroll()
  }

  open() {
    if (!this.element.hidden) return

    this.element.hidden = false
    this._lockScroll()
    openChrome(this, this.element)
    // Same double-rAF re-assert as due_deck_controller: the budget carousel
    // shows the tab bar for its first slide during load.
    requestAnimationFrame(() => requestAnimationFrame(() => openChrome(this, this.element)))
    this._entrance()
  }

  merge(event) {
    this._answer(event)
  }

  keep(event) {
    this._answer(event)
  }

  // One request per card, however many taps land while it is in flight.
  _answer(event) {
    const button = event.currentTarget
    const card = button.closest("[data-match-review-deck-target='card']")
    if (!card || card.dataset.busy) return

    card.dataset.busy = "1"
    this._setButtons(card, false)

    const request = this._post(button.dataset.url)
    this._requests.push(request)
    request.then((ok) => this._settle(card, ok))
  }

  _settle(card, ok) {
    if (!ok) {
      // Refused or never landed (offline, 5xx): the card stays, the next tap
      // tries again. The strip keeps its rows, which is the server's truth.
      delete card.dataset.busy
      this._setButtons(card, true)
      return
    }

    const leave = this._reducedMotion ? null : card.animate(
      [{ opacity: 1, transform: "translateY(0)" }, { opacity: 0, transform: "translateY(-6px)" }],
      { duration: 220, easing: "cubic-bezier(0.32, 0, 0.3, 1)", fill: "forwards" }
    )
    const gone = leave ? leave.finished.catch(() => {}) : Promise.resolve()
    gone.then(() => {
      card.remove()
      // The last pair answered closes the deck by itself.
      if (this.cardTargets.length === 0) this.later()
    })
  }

  later() {
    if (this.element.hidden) return

    this.element.hidden = true
    this._unlockScroll()
    document.dispatchEvent(new CustomEvent("celebration:closed"))

    if (this._requests.length === 0) {
      closeChrome(this)
      return
    }

    // Let every answer land before deciding, including one still in flight
    // when Later was tapped: any the server took means the dashboard has
    // changed and is re-fetched; none taken, close locally.
    Promise.allSettled(this._requests).then((results) => {
      const changed = results.some((result) => result.status === "fulfilled" && result.value)
      if (!changed) {
        closeChrome(this)
        return
      }

      closeChromeThenNavigate(this, () => window.Turbo.visit(window.location.href, { action: "replace" }))
    })
  }

  _setButtons(card, enabled) {
    card.querySelectorAll("button").forEach((button) => { button.disabled = !enabled })
  }

  // Resolves to whether the server took it.
  _post(url) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": token, Accept: "application/json" }
    }).then((response) => response.ok, () => false)
  }

  _entrance() {
    if (this._reducedMotion) {
      this.mascotTarget.style.cssText = "opacity: 1; transform: none"
      this.revealTargets.forEach((el) => { el.style.opacity = "1" })
      this.cardTargets.forEach((el) => { el.style.opacity = "1" })
      return
    }

    this.stageTarget.animate(
      [{ transform: "scale(0.9)", opacity: 0 }, { transform: "scale(1)", opacity: 1 }],
      { duration: 550, delay: 120, easing: "cubic-bezier(0.2, 1.05, 0.3, 1)", fill: "both" }
    )
    this.mascotTarget.animate(
      [{ transform: "translateY(30px) scale(0.7)", opacity: 0 }, { transform: "translateY(0) scale(1)", opacity: 1 }],
      { duration: 620, delay: 420, easing: this.constructor.EASE_SPRING, fill: "both" }
    )
    this.revealTargets.forEach((el, i) => {
      el.animate(
        [{ opacity: 0, transform: "translateY(10px)" }, { opacity: 1, transform: "translateY(0)" }],
        { duration: 460, delay: 620 + i * 70, easing: this.constructor.EASE_OUT, fill: "both" }
      )
    })
    // The pair cards follow the list in, one after another, from the moment
    // the list's own reveal starts (third reveal target: 620 + 2 * 70).
    this.cardTargets.forEach((el, i) => {
      el.animate(
        [{ opacity: 0, transform: "translateY(8px)" }, { opacity: 1, transform: "translateY(0)" }],
        { duration: 420, delay: 760 + i * 60, easing: this.constructor.EASE_OUT, fill: "both" }
      )
    })
  }

  // turbo:before-cache: hidden and unlocked, so a restored snapshot never
  // carries an open deck.
  _reset() {
    this.element.hidden = true
    this._unlockScroll()
  }

  get _reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  _lockScroll() {
    this._prevOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
  }

  // Only the lock this deck took is released (see review_prompt_controller).
  _unlockScroll() {
    if (this._prevOverflow === undefined) return

    document.documentElement.style.overflow = this._prevOverflow
    this._prevOverflow = undefined
  }
}
